/**
 * AddCustomerModal — M2.2.4 · Aggiungi al CRM Sarabi da conversazione WhatsApp.
 *
 * - Phone prefilled dal wa_id della conversazione.
 * - Riesegue check duplicati backend prima di creare.
 * - Se esiste già customer compatibile → link a quello (no duplicati).
 * - Se non esiste → crea customer + link contact.customer_id.
 * - `profile_name` WhatsApp NON viene proposto come nome.
 * - Nessuna modifica DB manuale.
 *
 * NON usa `create_customer` diretto — passa via endpoint dedicato
 * `POST /staff/messaging/whatsapp/conversations/{id}/link-customer`
 * che gestisce dedup + link atomicamente.
 */
import React, { useEffect, useState } from "react";
import { UserPlus, X, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export function AddCustomerModal({ conversationId, prefillPhone, matchStatus, onClose, onLinked }) {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [linkedExisting, setLinkedExisting] = useState(null);
  const [candidates, setCandidates] = useState(null);

  useEffect(() => {
    if (matchStatus !== "ambiguous") { return; }
    api.post(`/staff/messaging/whatsapp/conversations/${conversationId}/link-customer`, { name: "" })
      .then(({ data }) => { if (data.action === "ambiguous") { setCandidates(data.candidates || []); } })
      .catch((err) => setError(formatApiError(err)));
  }, [matchStatus, conversationId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError("Il nome è obbligatorio"); return; }
    setError(null); setLoading(true);
    try {
      const { data } = await api.post(
        `/staff/messaging/whatsapp/conversations/${conversationId}/link-customer`,
        {
          name: name.trim(),
          surname: surname.trim() || null,
          email: email.trim() || null,
        });
      // data.action = "created" | "linked_existing"
      if (data.action === "ambiguous") {
        setCandidates(data.candidates || []);
      } else if (data.action === "linked_existing") {
        setLinkedExisting(data.customer);
      } else {
        onLinked({ customer_id: data.customer_id,
                    customer_name: data.customer_name });
        onClose();
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const confirmExistingLink = async () => {
    setError(null); setLoading(true);
    try {
      const { data } = await api.post(
        `/staff/messaging/whatsapp/conversations/${conversationId}/link-existing-customer`,
        { customer_id: linkedExisting._id || linkedExisting.customer_id });
      onLinked({ customer_id: data.customer_id, customer_name: data.customer_name });
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="add-customer-modal"
         onClick={onClose}
         className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-3xl border-2 border-sand-300 shadow-2xl
                      w-full max-w-md p-6 relative">
        <button onClick={onClose}
                data-testid="add-customer-close"
                className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-sand-100">
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-full bg-bronze-100 border-2 border-bronze-300
                          flex items-center justify-center">
            <UserPlus className="w-5 h-5 text-bronze-700" />
          </div>
          <h2 className="font-serif text-2xl" data-testid="add-customer-title">
            {candidates ? "Verifica collegamento CRM" : "Aggiungi al CRM"}
          </h2>
        </div>

        {candidates && !linkedExisting ? (
          <CandidatePicker candidates={candidates} onPick={setLinkedExisting} onClose={onClose} />
        ) : linkedExisting ? (
          <div className="space-y-4">
            <div className="rounded-2xl border-2 border-green-200 bg-green-50 p-4 text-sm">
              <div className="flex items-center gap-2 text-green-800 font-semibold mb-1">
                <CheckCircle2 className="w-4 h-4" /> Cliente già in CRM
              </div>
              <div className="text-green-700">
                <div><b>{linkedExisting.name} {linkedExisting.surname || ""}</b></div>
                <div className="text-xs mt-1">Tel: {linkedExisting.phone}</div>
              </div>
            </div>
            <p className="text-sm text-ink-600">
              Vuoi collegare questa conversazione a questo cliente?
            </p>
            {error && (
              <div className="text-sm text-red-700" data-testid="link-existing-error">{error}</div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={onClose}
                      className="px-4 py-2 rounded-full border-2 border-sand-300
                                  text-sm hover:bg-sand-100">
                Annulla
              </button>
              <button onClick={confirmExistingLink}
                      disabled={loading}
                      data-testid="link-existing-confirm"
                      className="px-4 py-2 rounded-full bg-bronze-600 text-white
                                  text-sm font-semibold hover:bg-bronze-700">
                Collega
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink-500 mb-1">
                Telefono
              </label>
              <input type="text" value={prefillPhone} disabled
                     data-testid="add-customer-phone"
                     className="w-full rounded-full border-2 border-sand-300
                                bg-sand-100 px-4 py-2.5 text-sm text-ink-500" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink-500 mb-1">
                Nome *
              </label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                     required autoFocus
                     data-testid="add-customer-name"
                     className="w-full rounded-full border-2 border-sand-300
                                px-4 py-2.5 text-sm focus:border-bronze-500 outline-none" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink-500 mb-1">
                Cognome
              </label>
              <input type="text" value={surname} onChange={(e) => setSurname(e.target.value)}
                     data-testid="add-customer-surname"
                     className="w-full rounded-full border-2 border-sand-300
                                px-4 py-2.5 text-sm focus:border-bronze-500 outline-none" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink-500 mb-1">
                Email (opzionale)
              </label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     data-testid="add-customer-email"
                     className="w-full rounded-full border-2 border-sand-300
                                px-4 py-2.5 text-sm focus:border-bronze-500 outline-none" />
            </div>
            {error && (
              <div data-testid="add-customer-error"
                   className="rounded-2xl border-2 border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {error}
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={onClose}
                      className="px-4 py-2 rounded-full border-2 border-sand-300
                                  text-sm hover:bg-sand-100">
                Annulla
              </button>
              <button type="submit" disabled={loading}
                      data-testid="add-customer-submit"
                      className="px-4 py-2 rounded-full bg-bronze-600 text-white text-sm
                                  font-semibold hover:bg-bronze-700 disabled:opacity-50
                                  flex items-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Aggiungi al CRM
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function CandidatePicker({ candidates, onPick, onClose }) {
  return (
    <div className="space-y-4" data-testid="crm-candidates">
      <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        Più clienti CRM hanno questo numero. Scegli quello corretto: nessun collegamento automatico.
      </div>
      <ul className="space-y-2">
        {candidates.map((c) => (
          <li key={c._id}>
            <button type="button" onClick={() => onPick(c)}
                    data-testid={`crm-candidate-${c._id}`}
                    className="w-full text-left rounded-2xl border-2 border-sand-300 px-4 py-2.5
                               text-sm hover:border-bronze-500 hover:bg-sand-50 transition-colors">
              <div className="font-semibold">{c.name} {c.surname || ""}</div>
              <div className="text-xs text-ink-500">Tel: {c.phone}{c.email ? ` · ${c.email}` : ""}</div>
            </button>
          </li>
        ))}
      </ul>
      <div className="flex justify-end">
        <button type="button" onClick={onClose} data-testid="crm-candidates-cancel"
                className="px-4 py-2 rounded-full border-2 border-sand-300 text-sm hover:bg-sand-100">
          Annulla
        </button>
      </div>
    </div>
  );
}
